import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useRootStore } from '@/stores/rootStore';
import { useStoreOperation } from '@/composables/useStoreOperation';
import { getValidNextPages } from '@/constants/flows';
import type { PageName } from '@/constants/flows';
import type { ComponentPublicInstance, Ref } from 'vue';
import type ButtonGroup from '@/components/ButtonGroup.vue';
import { useConfigStore } from '@/stores/modules/configStore';
import { useFormStore } from '@/stores/modules/formStore';
import { usePrepopFormState } from '@/composables/usePrepopFormState';


type ButtonGroupRef = Ref<InstanceType<typeof ButtonGroup> | null>;

/**
 * Composable for handling page navigation with optional button animation
 * @param pageName - Current page identifier in the flow
 * @param errorMessage - Error message to display if navigation fails
 * @returns Navigation utilities including loading state and handler
 */
export function usePageNavigation(pageName: PageName, errorMessage: string) {
    const router = useRouter();
    const store = useRootStore();
    const { handleOperation } = useStoreOperation();
    const isNavigating = ref(false);

    async function retryRouterPush(router: any, route: any, maxAttempts = 3, delayMs = 1000) {
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                await router.push(route);
                return;
            } catch (error: any) {
                if (error.toString().includes('Failed to fetch dynamically imported module')) {
                    if (attempt === maxAttempts) {
                        throw new Error(`Failed to navigate after ${maxAttempts} attempts: ${error.message}`);
                    }
                    console.warn(`Navigation attempt ${attempt} failed, retrying in ${delayMs}ms...`);
                    await new Promise(resolve => setTimeout(resolve, delayMs));
                } else {
                    throw error;
                }
            }
        }
    }

    const handleNavigation = async (_buttonGroupRef?: ButtonGroupRef) => {
        console.log('Navigation started for page:', pageName);
        if (isNavigating.value) {
            console.log('Already navigating, returning');
            return;
        }
        isNavigating.value = true;

        // If we are on a page that is in the "to complete" list, remove it
        // For prepopulated forms, don't remove pages from pagesToComplete
        if (store.progress.pagesToComplete.includes(pageName) && store.progress.formType !== 'prePopulated') {
            store.progress.pagesToComplete = store.progress.pagesToComplete.filter(p => p !== pageName);
        }

        try {
            // Handle pre-pop-landing navigation
            if (pageName === 'pre-pop-landing') {
                console.log('Handling pre-pop-landing navigation');
                
                // For prepopulated forms, navigate to the next page in pagesToComplete array
                if (store.progress.formType === 'prePopulated') {
                    const pagesToComplete = store.progress.pagesToComplete;
                    if (pagesToComplete && pagesToComplete.length > 0) {
                        // Find current page index (pre-pop-landing should be at index 0)
                        const currentPageIndex = pagesToComplete.indexOf('pre-pop-landing');
                        const nextPageIndex = currentPageIndex + 1;
                        
                        // Navigate to next page if it exists
                        if (nextPageIndex < pagesToComplete.length) {
                            const nextPage = pagesToComplete[nextPageIndex];
                            const currentQuery = router.currentRoute.value.query;
                            const queryParams = Object.keys(currentQuery).length ? { query: currentQuery } : {};
                            
                            console.log('Pre-pop-landing navigation - Next page:', nextPage);
                            
                            // Handle pages that need parameters
                            if (nextPage === 'address-add' || nextPage === 'address-status' || nextPage === 'address-length') {
                                await retryRouterPush(router, { name: nextPage, params: { number: '1' }, ...queryParams });
                            } else if (nextPage === 'employment-location' || nextPage === 'employment-length') {
                                await retryRouterPush(router, { name: nextPage, params: { number: '1' }, ...queryParams });
                            } else {
                                await retryRouterPush(router, { name: nextPage, ...queryParams });
                            }
                            return;
                        }
                    }
                }
                
                // Fallback to default navigation for non-prepopulated forms
                console.log('Using default navigation for pre-pop-landing');
            }

            // Handle employment-length navigation
            if (pageName === 'employment-length') {
                console.log('Handling employment-length navigation');
                
                // For prepopulated forms, check employment history and navigate accordingly
                if (store.progress.formType === 'prePopulated') {
                    console.log('Using prepopulated navigation for employment-length');
                    const totalEmploymentHistory = store.form.totalEmploymentHistory;
                    const currentNumber = Number(router.currentRoute.value.params.number);
                    
                    console.log('Employment length navigation debug:', {
                        totalEmploymentHistory,
                        currentNumber,
                        formType: store.progress.formType
                    });
                    
                    if (totalEmploymentHistory >= 1) {
                        // Employment history is sufficient, follow pagesToComplete
                        const pagesToComplete = store.progress.pagesToComplete;
                        const currentQuery = router.currentRoute.value.query;
                        const queryParams = Object.keys(currentQuery).length ? { query: currentQuery } : {};
                        
                        if (pagesToComplete && pagesToComplete.length > 0) {
                            // Find current page in pagesToComplete and go to next
                            const currentPageIndex = pagesToComplete.indexOf(pageName);
                            if (currentPageIndex !== -1 && currentPageIndex < pagesToComplete.length - 1) {
                                const nextPage = pagesToComplete[currentPageIndex + 1];
                                await retryRouterPush(router, { name: nextPage, ...queryParams });
                            } else {
                                // Fallback to next page in sequence
                                const remainingPages = pagesToComplete.filter(page => page !== 'pre-pop-landing');
                                const nextPage = remainingPages.length > 0 ? remainingPages[0] : pagesToComplete[0];
                                await retryRouterPush(router, { name: nextPage, ...queryParams });
                            }
                        } else {
                            await retryRouterPush(router, { name: 'pre-pop-form', ...queryParams });
                        }
                    } else {
                        // Employment history insufficient, ask for previous employment
                        console.log('Employment history insufficient, asking for previous employment');
                        await retryRouterPush(router, { name: 'employment', params: { number: currentNumber + 1 } });
                    }
                    return;
                } else {
                    // Original logic for non-prepopulated forms
                    const totalEmploymentHistory = store.form.totalEmploymentHistory;
                    const currentNumber = Number(router.currentRoute.value.params.number);
                  
                    if (totalEmploymentHistory >= 1) {
                        // For non-prepopulated forms, follow the standard sequence
                        // employment-length should go to employment-income
                        await retryRouterPush(router, { name: 'employment-income', params: { number: '1' } });
                    } else {
                        await retryRouterPush(router, { name: 'employment', params: { number: currentNumber + 1 } });
                    }
                    return;
                }
            }

            // Special handling for employment navigation
            if (pageName === 'employment') {
                const currentEmploymentStatus = store.form.employments[
                    Number(router.currentRoute.value.params.number) - 1
                ]?.statusId;
                
                const nonWorkingStatuses = [12, 13, 19, 21, 16, 17];
                const nextRoute = nonWorkingStatuses.includes(currentEmploymentStatus ?? 0) 
                    ? 'employment-length' 
                    : 'employment-location';
                    
                await retryRouterPush(router, { 
                    name: nextRoute, 
                    params: { number: router.currentRoute.value.params.number }
                });
                return;
            }

            // Handle employment-location navigation
            if (pageName === 'employment-location') {
                if (store.progress.formType === 'dealerGetMyQuote') {
                    await retryRouterPush(router, { 
                        name: 'employment-address-add',
                        params: { number: router.currentRoute.value.params.number }
                    });
                } else {
                    await retryRouterPush(router, { 
                        name: 'employment-length',
                        params: { number: router.currentRoute.value.params.number }
                    });
                }
                return;
            }

            if (pageName === 'employment-income') {
                // For prepopulated forms, continue to next page without recalculating progress
                if (store.progress.formType === 'prePopulated') {
                    const pagesToComplete = store.progress.pagesToComplete;
                    const currentQuery = router.currentRoute.value.query;
                    const queryParams = Object.keys(currentQuery).length ? { query: currentQuery } : {};
                    
                    if (pagesToComplete && pagesToComplete.length > 0) {
                        // Find current page in pagesToComplete and go to next
                        const currentPageIndex = pagesToComplete.indexOf(pageName);
                        let nextPage: string;
                        
                        if (currentPageIndex !== -1 && currentPageIndex < pagesToComplete.length - 1) {
                            // Go to next page in sequence
                            nextPage = pagesToComplete[currentPageIndex + 1];
                        } else {
                            // Current page not in array or at end, go to first remaining page
                            const remainingPages = pagesToComplete.filter(page => page !== 'pre-pop-landing');
                            nextPage = remainingPages.length > 0 ? remainingPages[0] : pagesToComplete[0];
                        }
                        
                        console.log('Employment income complete, going to next page:', nextPage);
                        await retryRouterPush(router, { name: nextPage, ...queryParams });
                    } else {
                        // Fallback to contact details if no pages to complete
                        await retryRouterPush(router, { name: 'contact-details-p2', ...queryParams });
                    }
                } else {
                    // For non-prepopulated forms, always go to amount-to-finance after employment-income
                    // The user should complete the amount-to-finance step regardless of default values
                    console.log('Employment income complete, going to amount-to-finance');
                    await retryRouterPush(router, { name: 'amount-to-finance' });
                }
                return;
            }

            // Handle address-length navigation (only for non-prepopulated forms)
            if (pageName === 'address-length' && store.progress.formType !== 'prePopulated') {
                const currentNumber = Number(router.currentRoute.value.params.number);
                const currentAddress = store.form.addresses[currentNumber - 1];
                const currentAddressTime = (currentAddress?.yearsAtAddress || 0) + ((currentAddress?.monthsAtAddress || 0) / 12);
                
                console.log('Address Navigation Debug:', {
                    currentNumber,
                    currentAddress,
                    currentAddressTime,
                    allAddresses: store.form.addresses
                });
                
                // Calculate total address history excluding current address
                const previousAddressesTime = store.form.addresses.reduce((total, address, idx) => {
                    if (idx === currentNumber - 1) return total; // Skip current address
                    const years = address.yearsAtAddress || 0;
                    const months = address.monthsAtAddress || 0;
                    return total + years + (months / 12);
                }, 0);
                
                const totalAddressHistory = previousAddressesTime + currentAddressTime;
                
                console.log('Address History Calculation:', {
                    previousAddressesTime,
                    totalAddressHistory,
                    requiredHistory: 3
                });
                
                const pagesToComplete = store.progress.pagesToComplete;
                const currentQuery = router.currentRoute.value.query;
                const queryParams = Object.keys(currentQuery).length ? { query: currentQuery } : {};
                
                if (totalAddressHistory >= 3) {
                    console.log('Navigation: Moving to next non-address page');
                    // Filter out address pages from pagesToComplete
                    const nonAddressPages = (pagesToComplete || []).filter(
                        p => p !== 'address-add' && p !== 'address-status' && p !== 'address-length'
                    );
                    console.log('Available non-address pages:', nonAddressPages);
                    
                    if (nonAddressPages.length > 0) {
                        const nextPage = nonAddressPages[0];
                        await retryRouterPush(router, { name: nextPage, ...queryParams });
                    } else {
                        await retryRouterPush(router, { name: 'employment', params: { number: 1 } });
                    }
                } else {
                    console.log('Navigation: Moving to next address page');
                    await retryRouterPush(router, { name: 'address-add', params: { number: currentNumber + 1 } });
                }
                
                return;
            }

            console.log('Using default navigation logic');
            
            // First check if there are any pages to complete
            const pagesToComplete = store.progress.pagesToComplete;
            if (pagesToComplete && pagesToComplete.length > 0) {
                // For prepopulated forms, simply follow the pagesToComplete array in order
                if (store.progress.formType === 'prePopulated') {
                    const currentQuery = router.currentRoute.value.query;
                    const queryParams = Object.keys(currentQuery).length ? { query: currentQuery } : {};
                    
                    // Find the current page in the pagesToComplete array
                    const currentPageIndex = pagesToComplete.indexOf(pageName);
                    let nextPage: string;
                    
                    if (currentPageIndex === -1) {
                        // Current page not in array (likely completed), go to first remaining page
                        // Skip pre-pop-landing if we're not on it
                        const remainingPages = pagesToComplete.filter(page => page !== 'pre-pop-landing');
                        nextPage = remainingPages.length > 0 ? remainingPages[0] : pagesToComplete[0];
                    } else if (currentPageIndex < pagesToComplete.length - 1) {
                        // Go to next page in sequence
                        nextPage = pagesToComplete[currentPageIndex + 1];
                    } else {
                        // At the end of the array, go to first page (or handle completion)
                        nextPage = pagesToComplete[0];
                    }
                    
                    console.log('Prepopulated navigation - Current page index:', currentPageIndex, 'Next page:', nextPage, 'pagesToComplete:', pagesToComplete);
                    
                    // Handle pages that need parameters
                    if (nextPage === 'address-add' || nextPage === 'address-status' || nextPage === 'address-length') {
                        // For address pages, use the current address index from the route
                        const currentAddressNumber = router.currentRoute.value.params.number || '1';
                        await retryRouterPush(router, { name: nextPage, params: { number: currentAddressNumber }, ...queryParams });
                    } else if (nextPage === 'employment') {
                        // Employment status page needs parameter but should start with 1
                        await retryRouterPush(router, { name: nextPage, params: { number: '1' }, ...queryParams });
                    } else if (nextPage === 'employment-location' || nextPage === 'employment-length') {
                        await retryRouterPush(router, { name: nextPage, params: { number: '1' }, ...queryParams });
                    } else {
                        await retryRouterPush(router, { name: nextPage, ...queryParams });
                    }
                    return;
                }
                
                const nextPage = pagesToComplete[0];
                console.log('Next page from pagesToComplete:', nextPage);
                
                // Special handling for driving licence page
                if (pageName === 'driving-licence') {
                    console.log('Handling driving licence navigation');
                    const currentQuery = router.currentRoute.value.query;
                    const queryParams = Object.keys(currentQuery).length ? { query: currentQuery } : {};
                    
                    // If we're already on the driving licence page, move to the next page in pagesToComplete
                    if (nextPage === 'driving-licence') {
                        const nextPageInSequence = pagesToComplete[1];
                        console.log('Already on driving licence, moving to next page:', nextPageInSequence);
                        
                        if (nextPageInSequence === 'address-add') {
                            await retryRouterPush(router, { name: nextPageInSequence, params: { number: '1' }, ...queryParams });
                        } else if (nextPageInSequence === 'address-status' || nextPageInSequence === 'address-length' || nextPageInSequence === 'employment-location') {
                            await retryRouterPush(router, { 
                                name: nextPageInSequence, 
                                params: { number: router.currentRoute.value.params.number || '1' },
                                ...queryParams
                            });
                        } else {
                            await retryRouterPush(router, { name: nextPageInSequence, ...queryParams });
                        }
                    } else {
                        // Handle address-specific routing
                        if (nextPage === 'address-add') {
                            await retryRouterPush(router, { name: nextPage, params: { number: '1' }, ...queryParams });
                        } else if (nextPage === 'address-status' || nextPage === 'address-length' || nextPage === 'employment-location') {
                            await retryRouterPush(router, { 
                                name: nextPage, 
                                params: { number: router.currentRoute.value.params.number || '1' },
                                ...queryParams
                            });
                        } else {
                            await retryRouterPush(router, { name: nextPage, ...queryParams });
                        }
                    }
                    return;
                }
                
                // Special handling for marital status page
                if (pageName === 'marital-status') {
                    console.log('Handling marital status navigation');
                    const currentQuery = router.currentRoute.value.query;
                    const queryParams = Object.keys(currentQuery).length ? { query: currentQuery } : {};
                    
                    // If we're already on the marital status page, move to the next page in pagesToComplete
                    if (nextPage === 'marital-status') {
                        const nextPageInSequence = pagesToComplete[1];
                        console.log('Already on marital status, moving to next page:', nextPageInSequence);
                        
                        if (nextPageInSequence === 'address-add') {
                            await retryRouterPush(router, { name: nextPageInSequence, params: { number: '1' }, ...queryParams });
                        } else if (nextPageInSequence === 'address-status' || nextPageInSequence === 'address-length' || nextPageInSequence === 'employment-location') {
                            await retryRouterPush(router, { 
                                name: nextPageInSequence, 
                                params: { number: router.currentRoute.value.params.number || '1' },
                                ...queryParams
                            });
                        } else {
                            await retryRouterPush(router, { name: nextPageInSequence, ...queryParams });
                        }
                    } else {
                        // Handle address-specific routing
                        if (nextPage === 'address-add') {
                            await retryRouterPush(router, { name: nextPage, params: { number: '1' }, ...queryParams });
                        } else if (nextPage === 'address-status' || nextPage === 'address-length' || nextPage === 'employment-location') {
                            await retryRouterPush(router, { 
                                name: nextPage, 
                                params: { number: router.currentRoute.value.params.number || '1' },
                                ...queryParams
                            });
                        } else {
                            await retryRouterPush(router, { name: nextPage, ...queryParams });
                        }
                    }
                    return;
                }
                
                // Special handling for address-add page
                if (pageName === 'address-add') {
                    console.log('Handling address-add navigation');
                    const currentQuery = router.currentRoute.value.query;
                    const queryParams = Object.keys(currentQuery).length ? { query: currentQuery } : {};
                    
                    // If we're already on the address-add page, move to the next page in pagesToComplete
                    if (nextPage === 'address-add') {
                        const nextPageInSequence = pagesToComplete[1];
                        console.log('Already on address-add, moving to next page:', nextPageInSequence);
                        
                        if (nextPageInSequence === 'address-status' || nextPageInSequence === 'address-length' || nextPageInSequence === 'employment-location') {
                            await retryRouterPush(router, { 
                                name: nextPageInSequence, 
                                params: { number: router.currentRoute.value.params.number },
                                ...queryParams
                            });
                        } else {
                            await retryRouterPush(router, { name: nextPageInSequence, ...queryParams });
                        }
                    } else {
                        // Handle address-specific routing
                        if (nextPage === 'address-status' || nextPage === 'address-length' || nextPage === 'employment-location') {
                            await retryRouterPush(router, { 
                                name: nextPage, 
                                params: { number: router.currentRoute.value.params.number },
                                ...queryParams
                            });
                        } else {
                            await retryRouterPush(router, { name: nextPage, ...queryParams });
                        }
                    }
                    return;
                }
                
                // Special handling for address-status page
                if (pageName === 'address-status') {
                    console.log('Handling address-status navigation');
                    const currentQuery = router.currentRoute.value.query;
                    const queryParams = Object.keys(currentQuery).length ? { query: currentQuery } : {};
                    
                    // If we're already on the address-status page, move to the next page in pagesToComplete
                    if (nextPage === 'address-status') {
                        const nextPageInSequence = pagesToComplete[1];
                        console.log('Already on address-status, moving to next page:', nextPageInSequence);
                        
                        if (nextPageInSequence === 'address-length' || nextPageInSequence === 'employment-location') {
                            await retryRouterPush(router, { 
                                name: nextPageInSequence, 
                                params: { number: router.currentRoute.value.params.number },
                                ...queryParams
                            });
                        } else {
                            await retryRouterPush(router, { name: nextPageInSequence, ...queryParams });
                        }
                    } else {
                        // Handle address-specific routing
                        if (nextPage === 'address-length' || nextPage === 'employment-location') {
                            await retryRouterPush(router, { 
                                name: nextPage, 
                                params: { number: router.currentRoute.value.params.number },
                                ...queryParams
                            });
                        } else {
                            await retryRouterPush(router, { name: nextPage, ...queryParams });
                        }
                    }
                    return;
                }
                

                
                const currentQuery = router.currentRoute.value.query;
                const queryParams = Object.keys(currentQuery).length ? { query: currentQuery } : {};
                
                if (nextPage === pageName) {
                    const nextPageInSequence = pagesToComplete[1];
                    console.log('Already on page, moving to next page:', nextPageInSequence);
                    if (nextPageInSequence === 'address-add') {
                        await retryRouterPush(router, { name: nextPageInSequence, params: { number: '1' }, ...queryParams });
                    } else if (nextPageInSequence === 'address-status' || nextPageInSequence === 'address-length' || nextPageInSequence === 'employment-location') {
                        await retryRouterPush(router, { 
                            name: nextPageInSequence, 
                            params: { number: router.currentRoute.value.params.number },
                            ...queryParams
                        });
                    } else {
                        await retryRouterPush(router, { name: nextPageInSequence, ...queryParams });
                    }
                } else {
                    // Handle address-specific routing
                    if (nextPage === 'address-add') {
                        await retryRouterPush(router, { name: nextPage, params: { number: '1' }, ...queryParams });
                    } else if (nextPage === 'address-status' || nextPage === 'address-length' || nextPage === 'employment-location') {
                        await retryRouterPush(router, { 
                            name: nextPage, 
                            params: { number: router.currentRoute.value.params.number },
                            ...queryParams
                        });
                    } else {
                        await retryRouterPush(router, { name: nextPage, ...queryParams });
                    }
                }
                return;
            }

            // Fallback to valid next pages if no pages to complete
            const formType = store.progress.formType || 'direct';
            const validNextPages = getValidNextPages(pageName, formType);
            console.log('Valid next pages:', validNextPages);
            const nextPage = validNextPages[0];
            
            if (!nextPage) {
                throw new Error(`No valid next page found for ${pageName}`);
            }
            
            const currentQuery = router.currentRoute.value.query;
            const queryParams = Object.keys(currentQuery).length ? { query: currentQuery } : {};
            
            // Handle address-specific routing
            if (nextPage === 'address-add') {
                await retryRouterPush(router, { name: nextPage, params: { number: '1' }, ...queryParams });
            } else if (nextPage === 'address-status' || nextPage === 'address-length' || nextPage === 'employment-location') {
                await retryRouterPush(router, { 
                    name: nextPage, 
                    params: { number: router.currentRoute.value.params.number },
                    ...queryParams
                });
            } else {
                await retryRouterPush(router, { name: nextPage, ...queryParams });
            }
        } catch (error) {
            console.error('Navigation error:', error);
            throw error;
        } finally {
            isNavigating.value = false;
            console.log('Navigation completed for page:', pageName);
        }
    };

    return {
        isNavigating,
        handleNavigation
    };
} 